// Deep Research progress / summary panel shown on the assistant message.
// Live while the run is in flight; collapses to a one-line summary (click
// to expand) once the report is written.
import { useEffect, useState } from "react";
import { DEPTHS } from "./deep_research.js";

const PHASE_LABEL = {
  planning: "Planning the research",
  researching: "Researching",
  gaps: "Checking for gaps",
  writing: "Writing the report",
  done: "Research complete",
};

function fmtSecs(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export default function ResearchPanel({ research, S, live }) {
  const [open, setOpen] = useState(!!live);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [live]);
  if (!research) return null;
  const steps = Array.isArray(research.steps) ? research.steps : [];
  const done = steps.filter((s) => s.status !== "running").length;
  const elapsed = (research.finishedAt || Date.now()) - (research.startedAt || Date.now());
  const depthLabel = (DEPTHS[research.depth] || DEPTHS.standard).label;
  const expanded = live || open;
  const icon = (st) => st === "running" ? "◌" : st === "done" ? "✓" : st === "empty" ? "–" : "!";
  const iconColor = (st) => st === "done" ? (S.success || "#10b981") : st === "error" ? S.danger : S.text3;

  return (
    <div className="cf-research-panel" style={{ marginBottom: 10, background: S.inputBg, border: `1px solid ${S.border2}`, borderRadius: 10, overflow: "hidden" }}>
      <div onClick={() => !live && setOpen((o) => !o)} style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: live ? "default" : "pointer" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S.accent} strokeWidth="2"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16" y2="16" /><line x1="8" y1="11" x2="14" y2="11" /><line x1="11" y1="8" x2="11" y2="14" /></svg>
        <span style={{ flex: 1, color: S.text2, fontWeight: 600 }}>
          {live ? `${PHASE_LABEL[research.phase] || "Researching"}…` : `Deep research · ${steps.length} questions · ${research.sourceCount || 0} sources`}
        </span>
        <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace" }}>{depthLabel} · {fmtSecs(elapsed)}</span>
        {!live && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.5" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}><polyline points="6 9 12 15 18 9" /></svg>}
      </div>
      {live && (
        <div style={{ height: 3, background: S.border2 }}>
          <div style={{ height: 3, width: `${research.phase === "writing" ? 92 : research.phase === "planning" ? 8 : Math.min(88, 10 + (steps.length ? (done / steps.length) * 75 : 0))}%`, background: S.accent, transition: "width 0.4s" }} />
        </div>
      )}
      {expanded && steps.length > 0 && (
        <div style={{ padding: "6px 12px 10px", borderTop: `1px solid ${S.border}`, display: "flex", flexDirection: "column", gap: 4 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 11, lineHeight: 1.45 }}>
              <span style={{ width: 12, flexShrink: 0, color: iconColor(s.status), fontFamily: "monospace", fontWeight: 700 }}>{icon(s.status)}</span>
              <span style={{ flex: 1, color: s.status === "running" ? S.text : S.text2 }}>{s.question}</span>
              <span style={{ fontSize: 10, color: S.text3, fontFamily: "monospace", flexShrink: 0 }} title={s.error || ""}>
                {s.status === "running" ? "searching…" : s.status === "error" ? "failed" : `${s.sources} src`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
